// src/components/UpcomingEvents/UpcomingEvents.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaInfoCircle, FaMapMarkerAlt } from "react-icons/fa";
import { getUpcomingEvents } from "../../services/eventServices";
import Button from "../../components/Button/Button";
import { useUser } from "../../pages/Home/UserContext";
import { toAbsolutePhotoUrl } from "../../services/newsPhotoServices";
import SectionTitle from "../../components/ui/SectionTitle";

const FALLBACK = "/img/no-image.jpg";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

function toAbs(url) {
  const u = (url || "").trim();
  const abs = u ? toAbsolutePhotoUrl(u) : "";
  return abs || FALLBACK;
}

function formatParts(dateString) {
  if (!dateString) return null;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return null;

  // ex: 07, FÉV., 2026
  const day = d.toLocaleDateString("fr-FR", { day: "2-digit" });
  const month = d
    .toLocaleDateString("fr-FR", { month: "short" })
    .replace(".", "")
    .toUpperCase();
  const year = d.toLocaleDateString("fr-FR", { year: "numeric" });

  const full = d.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return { day, month, year, full };
}

function DateBadge({ start, end }) {
  const s = formatParts(start);
  const e = formatParts(end);

  if (!s) return null;

  const isRange =
    e && (e.day !== s.day || e.month !== s.month || e.year !== s.year);

  return (
    <div className="flex items-center gap-3">
      {/* “Calendar chip” moderne */}
      <div
        className={cn(
          "relative overflow-hidden rounded-2xl",
          "border border-slate-200/70 dark:border-slate-800",
          "bg-white/80 dark:bg-slate-900/60 backdrop-blur",
          "shadow-sm"
        )}
        title={s.full}
      >
        <div className="px-3 py-2">
          <div className="flex items-baseline gap-2">
            <div className="text-2xl font-extrabold leading-none text-slate-900 dark:text-slate-50">
              {s.day}
            </div>
            <div className="text-xs font-extrabold tracking-widest text-primary">
              {s.month}
            </div>
          </div>
          <div className="mt-1 text-[11px] font-semibold text-slate-600 dark:text-slate-300">
            {s.year}
          </div>
        </div>

        {/* petit accent animé discret */}
        <div
          aria-hidden
          className="h-1.5 w-full opacity-70"
          style={{
            background:
              "linear-gradient(to right, transparent, var(--tw-color-primary, #2563eb), transparent)",
          }}
        />
      </div>

      {/* Si date de fin : affichage “→ end” minimal */}
      {isRange && e ? (
        <div className="text-xs text-slate-700 dark:text-slate-200">
          <div className="font-semibold">au</div>
          <div className="mt-0.5 inline-flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800/60 px-3 py-1 border border-slate-200/70 dark:border-slate-800">
            <span className="text-sm font-extrabold text-slate-900 dark:text-slate-50">
              {e.day}
            </span>
            <span className="text-[11px] font-extrabold tracking-widest text-primary">
              {e.month}
            </span>
            <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
              {e.year}
            </span>
          </div>
        </div>
      ) : (
        <div className="text-xs text-slate-600 dark:text-slate-300 italic">
          {/* {s.full} */}
        </div>
      )}
    </div>
  );
}

const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    (async () => {
      try {
        const upcoming = await getUpcomingEvents();
        const list = Array.isArray(upcoming) ? upcoming : [];
        setEvents(list.slice(0, 3));
      } catch (e) {
        console.error("Failed to fetch events", e);
        setEvents([]);
      }
    })();
  }, []);

  const hasEvents = useMemo(() => events.length > 0, [events]);

  const onImgError = (e) => {
    if (e?.currentTarget) e.currentTarget.src = FALLBACK;
  };

  const openModal = (imageUrl) => setSelectedImage(toAbs(imageUrl));
  const closeModal = () => setSelectedImage(null);

  const handleMoreInfo = () => {
    if (!user) return;
    navigate("/calendrier");
  };

  return (
    <section
      className={cn(
        "py-12 sm:py-16",
        "bg-lightBackground dark:bg-darkBackground",
        "text-black dark:text-white"
      )}
      id="upcoming-events"
    >
      <div className="mx-auto max-w-7xl px-4">
        <SectionTitle
          title="Prochains événements & compétitions"
          subtitle={
            hasEvents
              ? "Découvrez les prochaines dates à ne pas manquer !"
              : "Aucun événement à venir pour le moment"
          }
        />

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {events.map((event) => {
            const cover = toAbs(event?.photo_url);
            const title = event?.titre || "Événement";
            const location = event?.lieu_event || "Lieu à préciser";

            return (
              <article
                key={event.id}
                className={cn(
                  "group overflow-hidden rounded-3xl",
                  "border border-slate-200/70 dark:border-slate-800",
                  "bg-white/80 dark:bg-slate-900/60 backdrop-blur",
                  "shadow-sm hover:shadow-xl hover:-translate-y-[2px]",
                  "transition duration-300 motion-reduce:transition-none"
                )}
              >
                {/* ✅ Image seule (aucun texte dessus) */}
                <button
                  type="button"
                  onClick={() => openModal(event?.photo_url)}
                  className="block w-full"
                  aria-label="Agrandir l'image"
                  title="Cliquer pour agrandir"
                >
                  <img
                    src={cover}
                    alt={title}
                    className={cn(
                      "h-56 w-full object-cover",
                      "transition duration-500 group-hover:scale-[1.02] motion-reduce:transition-none"
                    )}
                    onError={onImgError}
                    loading="lazy"
                    decoding="async"
                  />
                </button>

                <div className="p-5">
                  {/* ✅ Date moderne */}
                  <DateBadge start={event?.start} end={event?.end} />

                  {/* Titre (en dessous) */}
                  <h3 className="mt-4 text-xl font-extrabold leading-snug text-slate-900 dark:text-slate-50">
                    {title}
                  </h3>

                  {/* Lieu */}
                  <div className="mt-2 flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                    <FaMapMarkerAlt className="mt-0.5 shrink-0 opacity-80" />
                    <span className="leading-snug">{location}</span>
                  </div>

                  {/* Description */}
                  {event?.description ? (
                    <p className="mt-3 text-sm text-slate-700 dark:text-slate-200 leading-relaxed line-clamp-3">
                      {event.description}
                    </p>
                  ) : (
                    <p className="mt-3 text-sm text-slate-500 dark:text-slate-300 italic">
                      Description à venir…
                    </p>
                  )}

                  {/* CTA */}
                  {user ? (
                    <div className="mt-5 flex justify-center">
                      <Button
                        text="Plus d'infos"
                        onClick={handleMoreInfo}
                        icon={FaInfoCircle}
                        className="gap-2"
                        hoverLabel="Accéder au calendrier"
                      />
                    </div>
                  ) : null}
                </div>

                {/* accent bas discret */}
                <div
                  aria-hidden
                  className="h-1.5 w-full opacity-70 group-hover:opacity-100 transition"
                  style={{
                    background:
                      "linear-gradient(to right, transparent, var(--tw-color-primary, #2563eb), transparent)",
                  }}
                />
              </article>
            );
          })}
        </div>

        {/* ✅ Modale image : clic pour fermer + bouton X */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 px-3"
            onClick={closeModal}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="relative w-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage}
                alt="Agrandissement"
                className="w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl bg-black/20"
                onError={onImgError}
              />
              <button
                className="absolute -top-3 -right-3 h-11 w-11 rounded-full bg-white/90 text-slate-900 text-2xl grid place-items-center shadow-lg hover:text-primary transition"
                onClick={closeModal}
                aria-label="Fermer"
                type="button"
              >
                &times;
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default UpcomingEvents;
